'use client'

import { createContext, useContext, useMemo } from 'react'
import { useMachine } from '@xstate/react'
import { newMessagesListMachine } from './machines/newMessagesListMachine'

export type NewMessagesListMachineApi = ReturnType<typeof useMachine<typeof newMessagesListMachine>>

const NewMessagesListMachineContext = createContext<NewMessagesListMachineApi | null>(null)

export function NewMessagesListMachineProvider({ children }: { children: React.ReactNode }) {
  const api = useMachine(newMessagesListMachine)
  return (
    <NewMessagesListMachineContext.Provider value={api}>
      {children}
    </NewMessagesListMachineContext.Provider>
  )
}

export function useNewMessagesListMachine() {
  const ctx = useContext(NewMessagesListMachineContext)
  if (!ctx) {
    throw new Error('useNewMessagesListMachine must be used within NewMessagesListMachineProvider')
  }
  return ctx
}

export function useNewMessagesListMachineOptional() {
  return useContext(NewMessagesListMachineContext)
}

export function useNewMessagesActions() {
  const machineApi = useNewMessagesListMachine()
  const send = machineApi[1]

  return useMemo(() => ({
    addInteraction: (id: string) => send({ type: 'ADD_INTERACTION', id }),
    removeInteraction: (id: string) => send({ type: 'REMOVE_INTERACTION', id }),
    setInteractions: (ids: string[]) => send({ type: 'SET_INTERACTIONS', ids }),
    clear: () => send({ type: 'CLEAR' }),
  }), [send])
}

export function useNewMessagesListStoreCompat() {
  const machineApi = useNewMessagesListMachine()
  return useMemo(() => {
    const [state, send] = machineApi
    const ids = state.context.interactionsWithNewMessages
    return {
      interactionsWithNewMessages: ids,
      addInteraction: (id: string) => send({ type: 'ADD_INTERACTION', id }),
      removeInteraction: (id: string) => send({ type: 'REMOVE_INTERACTION', id }),
      setInteractions: (ids: string[]) => send({ type: 'SET_INTERACTIONS', ids }),
      clear: () => send({ type: 'CLEAR' }),
      hasNewMessages: (id: string) => ids.includes(id),
      getCount: () => ids.length,
    }
  }, [machineApi])
}
